import { inject, Injectable } from '@angular/core';
import { ActiveCartService, CartActions } from '@cart/data-access';
import { ServiceCodes } from '@checkout-v2/const';
import { CheckoutActions, CheckoutApiService } from '@checkout-v2/data-access';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { DeliveryAddressService, ServicesService } from '../services';

@Injectable()
export class ServicesEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly servicesService = inject(ServicesService);

  // Загрузить список услуг
  // Отправить запрос на бекенд
  loadServices$: Observable<CheckoutActions.LoadServicesSuccess | CheckoutActions.LoadServicesFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_SERVICES),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([, cartId]) =>
        this.checkoutApiService.loadServices(cartId).pipe(
          map((res) => new CheckoutActions.LoadServicesSuccess(res)),
          catchError(() => of(new CheckoutActions.LoadServicesFail())),
        ),
      ),
    ),
  );

  // Изменился адрес доставки (выбран и валиден)
  // Загрузить список услуг
  loadServicesAfterAddressChange$: Observable<CheckoutActions.LoadServices> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadSavedDeliveryAddressListSuccess>(
        CheckoutActions.LOAD_SAVED_DELIVERY_ADDRESS_LIST_SUCCESS,
      ),
      switchMap(() =>
        this.deliveryAddressService.activeAddress$.pipe(
          startWith(undefined),
          pairwise(),
          filter(
            ([prevAddress, currentAddress]) =>
              // todo: заменить JSON.stringify на явное сравнение полей
              !!currentAddress && JSON.stringify(prevAddress) !== JSON.stringify(currentAddress),
          ),
          concatLatestFrom(() => this.deliveryAddressService.isValid()),
          filter(([, isAddressValid]) => isAddressValid),
          // перед запросом услуг необходимо дождаться кастомер-консаймента и пересчета корзинки
          switchMap(() =>
            this.actions$.pipe(
              ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS),
              take(1),
              switchMap(() =>
                this.actions$.pipe(ofType<CartActions.LoadCartSuccess>(CartActions.LOAD_CART_SUCCESS), take(1)),
              ),
            ),
          ),
          map(() => new CheckoutActions.LoadServices()),
        ),
      ),
    ),
  );

  // Изменился адрес доставки (не выбран или не валиден)
  // Сбросить список услуг
  resetServicesAfterAddressChange$: Observable<CheckoutActions.ResetServices> = createEffect(() =>
    combineLatest({
      activeAddressId: this.deliveryAddressService.activeAddressId$,
      isValid: this.deliveryAddressService.isValid(),
    }).pipe(
      filter(({ activeAddressId, isValid }) => !activeAddressId || !isValid),
      distinctUntilChanged(
        (prev, current) => prev.activeAddressId === current.activeAddressId && prev.isValid === current.isValid,
      ),
      concatLatestFrom(() => this.servicesService.getServicesState()),
      filter(([, state]) => !!state.error || !!state.success),
      map(() => new CheckoutActions.ResetServices()),
    ),
  );

  // ------------------------------------

  // Загружен список услуг
  // Выбрать первую услугу, если выбранная услуга отсутствует в списке
  resetSelectedServiceOnServicesEmpty$: Observable<CheckoutActions.SelectServicesActiveOption> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadServicesSuccess>(CheckoutActions.LOAD_SERVICES_SUCCESS),
      map((action) => action.payload),
      concatLatestFrom(() => this.servicesService.getServicesActiveOptionCode()),
      map(([servicesList, selectedServiceCode]) => {
        const current: ServiceCodes | undefined = selectedServiceCode;
        let next: ServiceCodes | undefined;

        if (servicesList.length > 0) {
          next = (servicesList.find((item) => item.code === selectedServiceCode) ?? servicesList[0]).code;
        }

        return [current, next];
      }),
      filter(([current, next]) => current !== next),
      map(([, next]) => new CheckoutActions.SelectServicesActiveOption(next)),
    ),
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
